import React from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { colorFor } from '../../../utils/itemPalette';
import type { Bbox, ImageTransform } from '../useImageTransform';

/**
 * Absolute-positioned outlines + name labels per inventory item.
 *
 * Direct-manipulation model: each bbox AND each name label is a
 * `Pressable`. Tap → parent receives `onFocusBbox(id)` → the
 * focused bbox takes over the visual frame (everything else is
 * hidden so the image reads cleanly). Parent owns the cycle logic
 * (tap same id again → find next-larger containing bbox).
 *
 * Nested-bbox hit test: items are sorted SMALLEST-ON-TOP before
 * rendering (largest painted first, smallest last = highest DOM
 * z-order). This ensures a tap at a point where multiple bboxes
 * overlap hits the innermost — the Photoshop / Figma "click goes
 * to the topmost layer" convention, adapted for size.
 *
 * Refine mode overrides focus: when `editingItemId` is set, this
 * layer renders ONLY that item so the refine handles have no
 * visual competition.
 *
 * Labels anchor to the bbox top-left (flipped inside if clipped
 * by the image top). `maxWidth` clamps to the available width
 * from the bbox's left edge to the image render rect's right
 * edge, so labels never overflow the photo.
 */

export interface BboxLayerItem {
  id: number;
  bbox: Bbox;
  /** True if the item is in the accepted/keep set. */
  selected: boolean;
  /** Low-confidence or mask_error items get a dashed amber rim. */
  lowConfidence?: boolean;
  corrected?: boolean;
  /** Human-readable item name. Renders as the label pill. */
  name?: string;
}

interface BboxLayerProps {
  transform: ImageTransform;
  items: BboxLayerItem[];
  focusedItemId?: number | null;
  editingItemId?: number | null;
  /** Fires when the user taps a bbox or its name label. Parent
   *  decides what to do — set focus, cycle outward on repeat tap,
   *  toggle off at the outermost, etc. */
  onFocusBbox?: (id: number) => void;
  /** When true, focusing an item does NOT hide the other bboxes —
   *  they stay visible but unfocused (lower opacity, thinner border).
   *  Default false (segment-flow behavior: focus = isolate so the
   *  user can commit/edit one item without other rects competing).
   *  Browse-mode surfaces like SceneViewer set this to true so users
   *  can tap-tap-tap between bboxes without losing context. */
  keepUnfocusedVisible?: boolean;
}

const LABEL_HEIGHT = 20;
// FE #159 — raised from 80 → 140. Real item names are commonly
// 10-18 chars (e.g. "Dining table", "Laundry basket", "Woven basket"
// — all under 18) and the prior 80px clipped them to "Dini..." /
// "Laundry..." / "Woven ba...". 140px fits the long tail without
// overflowing the photo (we still cap to availableFromLeft).
const LABEL_MIN_WIDTH = 140;

export const BboxLayer: React.FC<BboxLayerProps> = ({
  transform,
  items,
  focusedItemId,
  editingItemId,
  onFocusBbox,
  keepUnfocusedVisible,
}) => {
  if (!transform.ready) return null;
  // Hide-everything-else rules — declutters the image when the
  // user has committed to one item:
  //   refine mode                          → render ONLY the editing item
  //   focus mode (segment / commit flow)   → render ONLY the focused item
  //   focus mode (browse / SceneViewer)    → render ALL items, but the
  //                                          focused one is styled
  //                                          stronger (via isFocused
  //                                          checks in the map below)
  //   default                              → render all items
  //
  // The browse-mode opt-in is `keepUnfocusedVisible`; refine mode
  // still isolates regardless because that flow needs an uncluttered
  // canvas for box drag handles.
  const visibleItems =
    typeof editingItemId === 'number'
      ? items.filter((it) => it.id === editingItemId)
      : typeof focusedItemId === 'number' && !keepUnfocusedVisible
        ? items.filter((it) => it.id === focusedItemId)
        : items;

  // Smallest-area-on-top z-ordering so inner nested bboxes catch
  // taps before their larger parents. Stable sort (by id as tie-
  // breaker) so equal-area siblings don't flicker on re-render.
  const ordered = [...visibleItems].sort((a, b) => {
    const areaA = a.bbox.w * a.bbox.h;
    const areaB = b.bbox.w * b.bbox.h;
    if (areaA !== areaB) return areaB - areaA; // largest → smallest
    return a.id - b.id;
  });

  const renderRect = transform.renderRect;
  const maxLabelRight = renderRect
    ? renderRect.x + renderRect.w - 2
    : Infinity;

  return (
    <View
      // box-none: empty areas pass clicks through to the image /
      // ZoomableStage below; bbox rects and labels catch taps.
      pointerEvents="box-none"
      style={StyleSheet.absoluteFill}
    >
      {ordered.map((it) => {
        const rect = transform.toDomBbox(it.bbox);
        const palette = colorFor(it.id);
        const isFocused = focusedItemId === it.id;
        const baseOpacity = it.selected ? 1 : 0.7;
        const opacity = isFocused ? 1 : baseOpacity;
        const stroke = it.selected ? palette.solid : `${palette.solid}b0`;
        const rimStyle = it.lowConfidence
          ? styles.lowConfidenceRim
          : null;

        // Flip label INSIDE the rect when the top-edge is clipped.
        const labelAbove = rect.y >= LABEL_HEIGHT + 2;
        const labelTop = labelAbove ? rect.y - LABEL_HEIGHT : rect.y + 2;
        const availableFromLeft = Math.max(40, maxLabelRight - rect.x);
        const labelMaxWidth = Math.max(
          40,
          Math.min(Math.max(rect.w, LABEL_MIN_WIDTH), availableFromLeft),
        );

        return (
          <React.Fragment key={`bbox-${it.id}`}>
            <Pressable
              onPress={() => onFocusBbox?.(it.id)}
              accessibilityRole="button"
              accessibilityLabel={`Focus ${it.name || 'item'}`}
              accessibilityState={{ selected: isFocused }}
              style={[
                styles.bbox,
                {
                  left: rect.x,
                  top: rect.y,
                  width: rect.w,
                  height: rect.h,
                  borderColor: stroke,
                  borderWidth: isFocused ? 3 : 2,
                  opacity,
                },
                rimStyle,
              ]}
            />
            {/* Hide the name label when this bbox is focused —
                the action strip + the rail card already convey
                identity, and the label was overlapping the strip
                on tight bboxes. */}
            {it.name && !isFocused ? (
              <Pressable
                onPress={(e: any) => {
                  if (e && typeof e.stopPropagation === 'function') {
                    e.stopPropagation();
                  }
                  onFocusBbox?.(it.id);
                }}
                accessibilityRole="button"
                accessibilityLabel={`Focus ${it.name}`}
                style={[
                  styles.label,
                  {
                    left: rect.x,
                    top: labelTop,
                    maxWidth: labelMaxWidth,
                    backgroundColor: palette.solid,
                    opacity,
                  },
                ]}
              >
                <Text
                  numberOfLines={1}
                  style={[styles.labelText, { color: palette.onSolid }]}
                >
                  {it.name}
                </Text>
              </Pressable>
            ) : null}
          </React.Fragment>
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  bbox: {
    position: 'absolute',
    borderRadius: 4,
    ...(Platform.OS === 'web'
      ? ({
          transition:
            'opacity 160ms ease-out, border-width 120ms ease-out, border-color 160ms ease-out',
          cursor: 'pointer',
        } as any)
      : null),
  },
  lowConfidenceRim: {
    borderStyle: 'dashed',
    borderColor: '#D97706',
  },
  label: {
    position: 'absolute',
    height: LABEL_HEIGHT,
    paddingHorizontal: 6,
    borderRadius: 4,
    justifyContent: 'center',
    ...(Platform.OS === 'web'
      ? ({
          transition: 'opacity 160ms ease-out',
          cursor: 'pointer',
        } as any)
      : null),
  },
  labelText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 11,
    lineHeight: 14,
    letterSpacing: 0.1,
  },
});
